/**
 * PCR master mix arithmetic.
 *
 * Each reagent's volume per reaction is  final ÷ stock × reaction volume  (C₁V₁ = C₂V₂ solved
 * for V₁, with stock and final in the same unit). The master mix is made for
 * N × (1 + overage) reactions so the last tube is not short after pipetting losses, the
 * template is added to each tube separately, and water makes up the rest.
 */

export type PcrUnit = "X" | "mM" | "uM" | "nM" | "U/uL" | "ng/uL" | "%";

export const PCR_UNITS: readonly PcrUnit[] = [
  "X",
  "mM",
  "uM",
  "nM",
  "U/uL",
  "ng/uL",
  "%",
];

/** Display text for each unit (the id keeps ASCII for stable option values). */
export const PCR_UNIT_LABELS: Record<PcrUnit, string> = {
  X: "X",
  mM: "mM",
  uM: "µM",
  nM: "nM",
  "U/uL": "U/µL",
  "ng/uL": "ng/µL",
  "%": "%",
};

export interface MixComponent {
  id: string;
  name: string;
  stock: number | null;
  final: number | null;
  unit: PcrUnit;
}

export type RowIssue = "stock" | "final" | "exceeds";

export interface MixRow {
  id: string;
  name: string;
  unit: PcrUnit;
  stock: number;
  final: number;
  perReactionUl: number;
  masterMixUl: number;
}

export type MixError = "volume" | "reactions" | "overage" | "template";

export interface MixPlan {
  rows: MixRow[];
  /** Components that could not be used, by id. */
  issues: { id: string; name: string; issue: RowIssue }[];
  waterPerReactionUl: number;
  waterMasterMixUl: number;
  /** Volume of master mix to dispense into each tube. */
  aliquotUl: number;
  templateUl: number;
  reactions: number;
  effectiveReactions: number;
  masterMixTotalUl: number;
  /** Water would be negative: the reagents and template do not fit. */
  overfullByUl: number;
}

export interface MixInput {
  volumeUl: number;
  reactions: number;
  overagePct: number;
  templateUl: number;
  components: readonly MixComponent[];
}

function classify(component: MixComponent): RowIssue | null {
  if (component.stock === null || !(component.stock > 0)) {
    return "stock";
  }
  if (component.final === null || !(component.final >= 0)) {
    return "final";
  }
  return component.final > component.stock ? "exceeds" : null;
}

export function planMasterMix(
  input: MixInput
): { plan: MixPlan } | { error: MixError } {
  if (!(input.volumeUl > 0)) {
    return { error: "volume" };
  }
  if (!(input.reactions >= 1)) {
    return { error: "reactions" };
  }
  if (!(input.overagePct >= 0)) {
    return { error: "overage" };
  }
  if (!(input.templateUl >= 0)) {
    return { error: "template" };
  }
  const effective = input.reactions * (1 + input.overagePct / 100);
  const rows: MixRow[] = [];
  const issues: MixPlan["issues"] = [];
  for (const component of input.components) {
    const issue = classify(component);
    if (issue !== null) {
      issues.push({ id: component.id, name: component.name, issue });
      continue;
    }
    const stock = component.stock as number;
    const final = component.final as number;
    const perReactionUl = (final / stock) * input.volumeUl;
    rows.push({
      id: component.id,
      name: component.name,
      unit: component.unit,
      stock,
      final,
      perReactionUl,
      masterMixUl: perReactionUl * effective,
    });
  }
  const used = rows.reduce((sum, row) => sum + row.perReactionUl, 0);
  const waterPerReactionUl = input.volumeUl - input.templateUl - used;
  const aliquotUl = input.volumeUl - input.templateUl;
  return {
    plan: {
      rows,
      issues,
      waterPerReactionUl,
      waterMasterMixUl: waterPerReactionUl * effective,
      aliquotUl,
      templateUl: input.templateUl,
      reactions: input.reactions,
      effectiveReactions: effective,
      masterMixTotalUl: aliquotUl * effective,
      overfullByUl: waterPerReactionUl < 0 ? -waterPerReactionUl : 0,
    },
  };
}

export type PresetNameKey =
  | "bufferTaq"
  | "dntps"
  | "primerFwd"
  | "primerRev"
  | "taq"
  | "bufferQ5"
  | "q5";

export type PresetId = "taq" | "q5" | "custom";

export interface PresetComponent {
  nameKey: PresetNameKey;
  stock: number;
  final: number;
  unit: PcrUnit;
}

export interface Preset {
  volumeUl: number;
  templateUl: number;
  components: readonly PresetComponent[];
}

/**
 * Starting points taken from NEB's protocol tables: Taq DNA Polymerase with Standard Taq
 * Buffer (M0273: 1X buffer, 200 µM dNTPs, 0.2 µM primers, 1.25 units per 50 µL from a
 * 5 U/µL stock) and Q5 High-Fidelity DNA Polymerase (M0491: 1X buffer, 200 µM dNTPs,
 * 0.5 µM primers, 0.02 U/µL from a 2 U/µL stock). The template is added per tube.
 */
export const PCR_PRESETS: Record<PresetId, Preset> = {
  taq: {
    volumeUl: 25,
    templateUl: 1,
    components: [
      { nameKey: "bufferTaq", stock: 10, final: 1, unit: "X" },
      { nameKey: "dntps", stock: 10, final: 0.2, unit: "mM" },
      { nameKey: "primerFwd", stock: 10, final: 0.2, unit: "uM" },
      { nameKey: "primerRev", stock: 10, final: 0.2, unit: "uM" },
      { nameKey: "taq", stock: 5, final: 0.025, unit: "U/uL" },
    ],
  },
  q5: {
    volumeUl: 25,
    templateUl: 1,
    components: [
      { nameKey: "bufferQ5", stock: 5, final: 1, unit: "X" },
      { nameKey: "dntps", stock: 10, final: 0.2, unit: "mM" },
      { nameKey: "primerFwd", stock: 10, final: 0.5, unit: "uM" },
      { nameKey: "primerRev", stock: 10, final: 0.5, unit: "uM" },
      { nameKey: "q5", stock: 2, final: 0.02, unit: "U/uL" },
    ],
  },
  custom: { volumeUl: 20, templateUl: 1, components: [] },
};
